import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CLUserApi } from 'api/CL/CLUserApi';
import { UserInfoApi } from 'models/CL/CLApiTypes';
import { useLoadingStore } from 'store/useLoadingStore';
import { useLoginStore } from 'store/useLoginDataStore';

export const CL_ALL_HOSPITAL_ID = 'all';

const getUserListData = (response: UserInfoApi) => {
    if (response.data) return response.data;

    const responseMessage = (response as unknown as { message?: string; error?: { message?: string } }).message
        || (response as unknown as { message?: string; error?: { message?: string } }).error?.message;

    throw new Error(responseMessage || `응답 데이터가 없습니다. status=${response.status || 'unknown'}`);
};

const getAxiosErrorMessage = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
        return error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다';
    }

    const responseData = error.response?.data as { message?: string; error?: { message?: string } } | undefined;
    return [
        error.response?.status,
        responseData?.message || responseData?.error?.message || error.message,
    ].filter(Boolean).join(' ');
};

export const useGetUserListQueries = (
    page: number,
    pageGroup: number,
    searchField: string,
    searchKeyword: string,
    sortField: string,
    sortOrder: string,
    isFavorite: boolean | null,
    hospitalId?: string
) => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const limitProps = 12;
    const selectedHospitalId = hospitalId || loginData.hospitalId;
    const clAdminToken = process.env.REACT_APP_CL_ADMIN_KEY;

    return useQuery({
        // 쿼리 키 배열로 정의
        queryKey: [
            'getUserListQueries',
            page,
            pageGroup,
            searchField,
            searchKeyword,
            sortField,
            sortOrder,
            isFavorite,
            selectedHospitalId,
            loginData.id,
            loginData.level,
        ],
        // 쿼리 함수
        queryFn: async () => {
            try {
                setLoading(true);
                const response = selectedHospitalId === CL_ALL_HOSPITAL_ID
                    ? await CLUserApi.getAllUserList(
                        page,
                        searchField,
                        searchKeyword,
                        sortField,
                        sortOrder,
                        limitProps,
                        clAdminToken!,
                        isFavorite
                    )
                    : await CLUserApi.getUserList(
                        page,
                        searchField,
                        searchKeyword,
                        sortField,
                        sortOrder,
                        limitProps,
                        clAdminToken!,
                        selectedHospitalId,
                        isFavorite
                    );
                return getUserListData(response);
            } catch (error) {
                throw new Error(getAxiosErrorMessage(error));
            } finally {
                setLoading(false);
            }
        },
        retry: 1, // 무한 재시도 방지 (true는 무한 재시도를 의미)
        refetchOnWindowFocus: false, // 창 포커스 시 재요청 방지
        enabled: !!selectedHospitalId && !!clAdminToken,
        // staleTime: 5 * 60 * 1000,    // 5분간 데이터를 '신선한' 상태로 유지 (불필요한 재요청 방지)
        // gcTime: 10 * 60 * 1000,      // 10분간 비활성 쿼리 데이터 유지
    });
};
